import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Info, Loader, TimerOff, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import dayjs from 'dayjs';
import { enUS, pl } from 'react-day-picker/locale';
import omit from 'lodash/omit';
import type { ChangeEvent } from 'react';
import type { CartItem } from '@/lib/types/cart.types.ts';
import type { SlotsResponse, TimeSlot } from '@/lib/types/order.types.ts';
import cartStore from '@/stores/cart.store.ts';
import { getAvailableSlots, getServices, getServicesWithSubServices } from '@/lib/server-fns.ts';
import orderStore from '@/stores/order.store.ts';

const { VITE_LOCALE } = import.meta.env;

export default function ServiceDateModal() {
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
	const [isTimeSlotsPending, setIsTimeSlotsPending] = useState(false);
	const [slotsResponse, setSlotsResponse] = useState<SlotsResponse>({});
	const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
	const [services, setServices] = useState<Array<any>>([]);
	const [servicesWithPhoto, setServicesWithPhoto] = useState<Array<any>>([]);
	const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
	const [tempService, setTempService] = useState<CartItem | null>(null);
	const [month, setMonth] = useState<Date | undefined>(undefined);

	const { t } = useTranslation();

	const { addItem, updateItem, items } = cartStore(
		useShallow((state) => ({
			updateItem: state.updateItem,
			addItem: state.addItem,
			items: state.items,
		})),
	);

	const { selectedService, setSelectedService } = orderStore(
		useShallow((state) => ({
			setSelectedService: state.setSelectedService,
			selectedService: state.selectedService,
		})),
	);

	const currencyFormatter = new Intl.NumberFormat(VITE_LOCALE, {
		style: 'currency',
		currency: VITE_LOCALE === 'pl' ? 'PLN' : 'PLN',
	});

	const targetService = services?.find((service) => `${service?.id}` === selectedId);
	const targetServiceWithPhoto = servicesWithPhoto?.find(
		(service) => `${service?.id}` === selectedId,
	);
	const subServices = targetService?.subServices || [];

	const filteredServices = services.filter(
		(service) =>
			!service?.subServices?.every((sub: any) => items?.some((item) => item?.id === sub?.id)),
	);

	const filteredSubServices = subServices.filter(
		(subService: any) => !items?.some((item) => item?.id === subService?.id),
	);

	const itemsByDate = items?.filter((item) => item?.date?.day === slotsResponse?.date);

	const renderService = selectedService?.id ? selectedService : tempService;

	const filteredSlots = slotsResponse?.slots?.filter(
		(slot) =>
			!itemsByDate?.some(
				(item) =>
					item?.date?.slot?.startTime === slot?.startTime &&
					item?.date?.slot?.endTime === slot?.endTime &&
					item?.id !== renderService?.id,
			),
	);

	const price = currencyFormatter.format(renderService?.price || 0);

	const handleGetTimeSlots = async (start: string, end: string) => {
		try {
			setIsTimeSlotsPending(true);
			const response = await getAvailableSlots({ data: { start, end } });
			setSlotsResponse(response?.[0]);
		} catch (error) {
			console.error(error);
		} finally {
			setIsTimeSlotsPending(false);
		}
	};

	const handleSelectDate = (date: Date | undefined) => {
		setSelectedDate(date);

		const start = dayjs(date).format('YYYY-MM-DD');
		const end = dayjs(date).add(1, 'day').format('YYYY-MM-DD');

		if (!date) {
			setSelectedSlot(null);
			setSlotsResponse({});
			return;
		}

		setMonth(date);
		setSelectedSlot(null);
		void handleGetTimeSlots(start, end);
	};

	const onClose = () => {
		setSelectedService(null);
		setSelectedDate(undefined);
		setSelectedSlot(null);
		setSlotsResponse({});
		setServices([]);
		setServicesWithPhoto([]);
		setSelectedId(undefined);
		setTempService(null);
		setMonth(new Date());
	};

	const handleConfirm = () => {
		const day = dayjs(selectedDate).format('YYYY-MM-DD');

		if (!day || !selectedSlot || !selectedService) return;

		if (selectedService?.id) {
			updateItem({ id: selectedService?.id, date: { day, slot: selectedSlot } });
		} else {
			const payload = { ...tempService, date: { day, slot: selectedSlot } } as CartItem;
			addItem(omit(payload, 'toAdd'), !tempService?.toAdd);
		}

		onClose();
	};

	const handleServiceChoose = (evt: ChangeEvent<HTMLSelectElement>) => {
		const id = evt?.target?.value;
		const targetSubService = subServices.find((service: any) => `${service.id}` === id);

		setTempService({
			...targetSubService,
			service: targetService?.name,
			serviceImage: targetServiceWithPhoto?.photoUrl,
			serviceId: targetService?.id,
		});
	};

	useEffect(() => {
		if (!selectedService?.date) return;
		if (selectedDate || selectedSlot) return;

		handleSelectDate(new Date(dayjs(selectedService?.date?.day).toISOString()));
		setSelectedSlot(selectedService?.date?.slot || null);
	}, [selectedService]);

	useEffect(() => {
		if (selectedService === null) return;
		if (selectedService?.id) return;

		void getServicesWithSubServices().then((response: Array<any>) => {
			setServices(response);
		});

		void getServices().then((response: Array<any>) => {
			setServicesWithPhoto(response);
		});
	}, [selectedService]);

	useEffect(() => {
		if (!selectedService?.toAdd) return;
		setTempService(selectedService as CartItem);
		setSelectedService({});
	}, [selectedService]);

	return (
		<div className="service-date-modal z-200" data-visible={!!selectedService}>
			<div
				className="cart-overlay fixed flex items-center p-4 justify-center top-0 left-0 w-full h-full max-sm:p-0 transition-all opacity-0 invisible data-[visible=true]:visible data-[visible=true]:opacity-100 backdrop-blur-[4px]  bg-black/20 z-50"
				onClick={onClose}
				data-visible={!!selectedService}
			>
				<div
					className="p-4 rounded-xl select-none max-sm:rounded-none bg-white max-sm:absolute max-sm:top-0 max-sm:left-0 max-sm:h-[100dvh] w-[700px] max-w-[100%] max-h-[100%] overflow-y-auto flex flex-col gap-4"
					onClick={(event) => {
						event.stopPropagation();
					}}
				>
					<div className="flex items-center justify-between gap-2 mb-2">
						<h2 className="text-2xl max-sm:text-xl text-[var(--primary)]">
							{t('order.choose_date')}
						</h2>

						<button
							className="cursor-pointer rounded-xl border border-neutral-200 p-2 text-neutral-600 transition-all hover:border-orange-600 active:scale-97"
							onClick={onClose}
						>
							<X />
						</button>
					</div>

					{renderService ? (
						<div className="flex flex-col gap-2">
							<span className="font-medium text-lg text-[var(--primary)] truncate">
								{renderService?.service}
							</span>

							<div key={renderService?.id} className="w-full p-3 bg-neutral-100 rounded-xl">
								<div className="flex flex-col gap-2">
									<div className="flex items-center justify-start gap-4">
										<div className="w-16 h-16 shrink-0 flex items-center justify-center bg-neutral-200 rounded-xl overflow-hidden">
											<img
												className="w-full h-full object-contain data-[cover=true]:object-cover"
												src={renderService?.serviceImage || '/icons/service.png'}
												alt={renderService?.name || '•••••••••••••••'}
												data-cover={!!renderService?.serviceImage}
											/>
										</div>

										<div className="flex flex-col gap-0">
											<p className="text-sm font-bold text-neutral-700 truncate max-w-[280px] max-md:max-w-[220px] max-sm:max-w-[160px]">
												{renderService?.name || '•••••••••••••••'}
											</p>

											<span className="text-sm text-neutral-500">
												{t('services.sub_service_start_from', { qty: price })}
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="w-full">
							<div className="select">
								<select
									id="countries"
									className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600  pl-4 border-2 border-neutral-100 pr-10 data-[highlight=true]:border-[var(--primary)]"
									name="year"
									data-highlight={!selectedId || selectedId === t('order.service_type')}
									value={selectedId}
									onChange={(evt) => {
										setSelectedId(evt?.target?.value);
									}}
								>
									<option defaultValue={t('order.service_type')}>{t('order.service_type')}</option>

									{filteredServices?.map((service) => (
										<option key={service?.id} value={service?.id}>
											{service?.name}
										</option>
									))}
								</select>
							</div>

							{selectedId && selectedId !== t('order.service_type') && (
								<div className="select mt-2">
									<select
										id="countries"
										className="w-full bg-neutral-100 rounded-xl h-12 text-neutral-600  pl-4 pr-10 border-2 border-neutral-100 appearance-none data-[highlight=true]:border-[var(--primary)]"
										name="year"
										data-highlight={!renderService}
										onChange={handleServiceChoose}
									>
										<option defaultValue={t('order.select_service')}>
											{t('order.select_service')}
										</option>

										{filteredSubServices?.map((item: any) => (
											<option key={item?.id} value={item?.id}>
												{item?.name}
											</option>
										))}
									</select>
								</div>
							)}
						</div>
					)}

					<div className="h-[340px] max-sm:h-full max-sm:flex-1 flex gap-2 max-sm:flex-wrap relative">
						<DayPicker
							disabled={{ before: new Date() }}
							onSelect={handleSelectDate}
							selected={selectedDate}
							mode="single"
							month={month}
							onMonthChange={setMonth}
							locale={VITE_LOCALE === 'pl' ? pl : enUS}
							className="w-fit max-sm:w-full h-full max-sm:h-fit border-2 border-neutral-100 px-4 py-2 rounded-xl max-sm:data-[hidden=true]:!invisible data-[disabled=true]:pointer-events-none data-[disabled=true]:grayscale-100 data-[disabled=true]:opacity-60 data-[highlight=true]:border-[var(--primary)]"
							data-hidden={!!selectedDate}
							data-disabled={!renderService}
							data-highlight={
								(selectedId && renderService && !selectedDate) || (renderService && !selectedDate)
							}
							classNames={{
								root: 'rdp-root',
								months: 'max-w-unset relative',
								weekday: 'text-neutral-600 relative h-12',
								month_grid: 'w-full',
								day: 'text-[lg] rounded-xl text-neutral-600 max-md:text-sm hover:bg-neutral-100',
								day_button: '!w-full min-w-11 max-md:min-w-9 max-sm:min-w-6 rdp-day_button',
								today: '!text-[var(--primary)] font-bold',
								selected: '!bg-[var(--primary)] !text-white rounded-xl',
								button_previous:
									'w-10 h-10 group mr-2 rounded-xl flex items-center justify-center text-neutral-200 border border-neutral-200 cursor-pointer active:scale-97 hover:border-[var(--primary)] transition-all',
								button_next:
									'w-10 h-10 group rounded-xl flex items-center justify-center text-neutral-200 border border-neutral-200 cursor-pointer active:scale-97 hover:border-[var(--primary)] transition-all',
								chevron: 'fill-neutral-500 transition-all group-hover:fill-[var(--primary)]',
							}}
						/>

						<div
							className="w-full h-full  border-2 border-neutral-100 rounded-xl max-sm:absolute z-10 bg-white max-sm:data-[hidden=true]:invisible data-[highlight=true]:border-[var(--primary)]"
							data-hidden={!selectedDate}
							data-highlight={
								(selectedId && renderService && selectedDate && !selectedSlot) ||
								(renderService && selectedDate && !selectedSlot)
							}
						>
							<div className="py-2 px-4 flex items-center gap-4 border-b border-neutral-200 min-sm:hidden">
								<button
									className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-neutral-100 hover:brightness-95 active:scale-97 cursor-pointer"
									onClick={() => {
										setSelectedDate(undefined);
										setSelectedSlot(null);
									}}
								>
									<ChevronLeft />
								</button>

								<span className="text-lg text-center font-medium text-neutral-600">
									{t('order.available_time')}
								</span>
							</div>

							{isTimeSlotsPending ? (
								<div className="w-full h-full max-sm:h-[calc(100%_-_57px)] flex items-center justify-center text-[var(--primary)]">
									<Loader size="2rem" className="animate-spin" />
								</div>
							) : filteredSlots?.length && !isTimeSlotsPending ? (
								<div
									className="w-full h-full max-sm:h-[calc(100%_-_73px)] flex flex-col overflow-y-auto gap-2 p-4"
									key={`${selectedDate}`}
								>
									{filteredSlots?.map((slot, i) => {
										const isActive =
											slot?.startTime === selectedSlot?.startTime &&
											slot?.endTime === selectedSlot?.endTime;

										return (
											<button
												className="text-center p-2 bg-neutral-200/50 text-neutral-700 text-sm rounded-xl hover:bg-[var(--primary)]/30 cursor-pointer active:scale-97 data-[active=true]:bg-[var(--primary)]/30"
												key={`${slot.startTime}-${i}`}
												data-active={isActive}
												ref={(ref) => {
													if (!isActive) return;
													ref?.scrollIntoView({ behavior: 'smooth', block: 'center' });
												}}
												onClick={() => {
													setSelectedSlot(slot);
												}}
											>
												{slot?.startTime} - {slot?.endTime}
											</button>
										);
									})}
								</div>
							) : selectedDate ? (
								<div className="w-full h-full max-sm:h-[calc(100%_-_57px)] whitespace-break-spaces flex flex-col gap-4 items-center justify-center text-neutral-400 text-center">
									<TimerOff size="5rem" className="text-neutral-300" />

									{t('order.not_available')}
								</div>
							) : (
								<div className="w-full h-full flex items-center justify-center text-neutral-400">
									{t('order.time_slots')}
								</div>
							)}
						</div>
					</div>

					<div className="p-2 px-4 flex items-center gap-4 text-sm bg-[var(--primary)]/10 rounded-xl whitespace-break-spaces text-neutral-500">
						<Info className="shrink-0" />

						{t('order.pre_order_caption')}
					</div>

					<div className="flex items-center gap-2 justify-end max-sm:mt-auto">
						<button
							className="py-2 max-sm:flex-auto text-neutral-600 px-4 goldman bg-[var(--primary)]/30 cursor-pointer hover:bg-[var(--primary)]/40 rounded-xl active:scale-97 select-none"
							onClick={onClose}
						>
							{t('order.cancel')}
						</button>

						<button
							className="py-2 max-sm:flex-auto px-4 goldman bg-[var(--primary)]/90 cursor-pointer hover:bg-[var(--primary)] text-white rounded-xl active:scale-97 select-none disabled:bg-neutral-400 disabled:pointer-events-none"
							disabled={!selectedSlot || !renderService?.id}
							onClick={handleConfirm}
						>
							{t('order.confirm')}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
